import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Plus, X, Trash2 } from "lucide-react";
import {
  useQuotes,
  useQuote,
  useCreateQuote,
  useVoidQuote,
  useConvertQuote,
  QUOTE_STATUS_LABEL,
  QuoteStatus,
} from "@/api/quotes";
import { useStoreCustomers, useOpenShift, PAYMENT_METHOD_LABEL, PaymentMethod } from "@/api/pos";
import { useProducts, useBranches } from "@/api/inventory";
import { getApiErrorMessage, getApiErrorCode } from "@/api/client";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

const STATUS_STYLE: Record<QuoteStatus, string> = {
  ABIERTA: "bg-blue-100 text-blue-700",
  CONVERTIDA: "bg-green-100 text-green-700",
  ANULADA: "bg-red-100 text-red-700",
};

const QUICK_PAYMENT_METHODS: PaymentMethod[] = ["EFECTIVO", "TARJETA", "TRANSFERENCIA", "NEQUI", "DAVIPLATA", "CREDITO"];

/** Cotizaciones de tienda: precio estimado para un cliente, sin tocar stock ni requerir turno de caja abierto. Se convierte en una venta real del POS cuando el cliente acepta. */
export default function QuotesPage() {
  const [status, setStatus] = useState<QuoteStatus | "">("ABIERTA");
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = useQuotes({ status: status || undefined, pageSize: 50 });
  const quotes = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Cotizaciones</h1>
          <p className="text-sm text-gray-500">Da un precio estimado a un cliente sin afectar el inventario; conviértela en venta cuando acepte.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} className="mr-1.5" /> Nueva cotización
        </button>
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <select className="input-field w-auto" value={status} onChange={(e) => setStatus(e.target.value as QuoteStatus | "")}>
          <option value="">Todos los estados</option>
          {Object.entries(QUOTE_STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">#</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Fecha</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Vigencia</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Total</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && quotes.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  No hay cotizaciones con este filtro.
                </td>
              </tr>
            )}
            {quotes.map((q) => (
              <tr key={q.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelectedId(q.id)}>
                <td className="px-4 py-3 text-gray-600">#{q.quoteNumber}</td>
                <td className="px-4 py-3 text-gray-600">{q.quoteDate}</td>
                <td className="px-4 py-3 text-gray-900">{q.storeCustomer?.name ?? q.customerName ?? "—"}</td>
                <td className="px-4 py-3 text-gray-600">
                  {q.validUntil ?? "—"} {q.isExpired && <span className="ml-1 text-xs font-medium text-red-600">(vencida)</span>}
                </td>
                <td className="px-4 py-3 text-right text-gray-900">{currency.format(Number(q.total))}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[q.status]}`}>{QUOTE_STATUS_LABEL[q.status]}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => setSelectedId(q.id)}>
                    Ver
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateQuoteModal onClose={() => setShowCreate(false)} />}
      {selectedId && <QuoteDetailModal id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function QuoteDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: quote, isLoading } = useQuote(id);
  const voidQuote = useVoidQuote();
  const convertQuote = useConvertQuote();
  const { data: openShift } = useOpenShift(quote?.branchId);

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("EFECTIVO");
  const [amountPaid, setAmountPaid] = useState("0");

  useEffect(() => {
    if (quote) setAmountPaid(String(Number(quote.total)));
  }, [quote?.id, quote?.total]);

  const isCredit = paymentMethod === "CREDITO";
  const canUseCredit = !!quote?.storeCustomerId;

  const handleVoid = async () => {
    if (!window.confirm("¿Anular esta cotización?")) return;
    try {
      await voidQuote.mutateAsync(id);
      toast.success("Cotización anulada.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleConvert = async () => {
    if (isCredit && !canUseCredit) {
      toast.error("Para convertir a crédito la cotización necesita un cliente registrado.");
      return;
    }
    try {
      await convertQuote.mutateAsync({ id, paymentMethod, amountPaid });
      toast.success("Cotización convertida en venta.");
      onClose();
    } catch (error) {
      // El precio se recalcula VIGENTE al convertir — puede haber quedado por debajo del costo
      // aunque no lo estuviera al crear la cotización (ver pos.service.ts::createSale).
      if (getApiErrorCode(error) === "BELOW_COST_CONFIRMATION_REQUIRED") {
        if (window.confirm(`${getApiErrorMessage(error)}\n\n¿Convertir de todas formas?`)) {
          try {
            await convertQuote.mutateAsync({ id, paymentMethod, amountPaid, acknowledgeBelowCost: true });
            toast.success("Cotización convertida en venta.");
            onClose();
          } catch (retryError) {
            toast.error(getApiErrorMessage(retryError));
          }
        }
        return;
      }
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Cotización {quote ? `#${quote.quoteNumber}` : ""}</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
          {quote && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Cliente</p>
                  <p className="font-medium text-gray-900">{quote.storeCustomer?.name ?? quote.customerName ?? "—"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Sucursal</p>
                  <p className="font-medium text-gray-900">{quote.branch?.name ?? "—"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Vigencia</p>
                  <p className="font-medium text-gray-900">
                    {quote.validUntil ?? "Sin vencimiento"} {quote.isExpired && <span className="text-red-600">(vencida)</span>}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Total</p>
                  <p className="font-semibold text-gray-900">{currency.format(Number(quote.total))}</p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-sm font-medium text-gray-700">Productos</p>
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-500">Producto</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-500">Cant.</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-500">Precio</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-500">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {quote.lines.map((l) => (
                      <tr key={l.id}>
                        <td className="px-2 py-1.5">{l.product?.name ?? l.productId}</td>
                        <td className="px-2 py-1.5 text-right">{l.quantity}</td>
                        <td className="px-2 py-1.5 text-right">{currency.format(Number(l.unitPrice))}</td>
                        <td className="px-2 py-1.5 text-right">{currency.format(Number(l.subtotal))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {quote.notes && (
                <div>
                  <p className="text-xs font-medium text-gray-500">Notas</p>
                  <p className="text-sm text-gray-700">{quote.notes}</p>
                </div>
              )}

              {quote.status === "CONVERTIDA" && (
                <p className="rounded-md bg-green-50 p-3 text-sm text-green-700">
                  Convertida en la venta #{quote.sale?.saleNumber ?? "—"}.
                </p>
              )}

              {quote.status === "ABIERTA" && (
                <div className="space-y-2 rounded-md bg-gray-50 p-3">
                  <p className="text-sm font-medium text-gray-700">Convertir en venta</p>
                  {!openShift && (
                    <p className="text-xs text-amber-700">
                      No hay un turno de caja abierto en {quote.branch?.name ?? "esta sucursal"}. Abre un turno en el POS antes de convertir.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {QUICK_PAYMENT_METHODS.filter((m) => m !== "CREDITO" || canUseCredit).map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={`rounded-md border px-2.5 py-1 text-xs font-medium ${paymentMethod === m ? "border-brand-600 bg-brand-50 text-brand-700" : "border-gray-200 text-gray-600"}`}
                        onClick={() => setPaymentMethod(m)}
                      >
                        {PAYMENT_METHOD_LABEL[m]}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input className="input-field" type="number" placeholder="Monto pagado" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} />
                    {!isCredit && (
                      <button type="button" className="btn-secondary" onClick={() => setAmountPaid(String(Number(quote.total)))}>
                        Todo
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button type="button" className="btn-secondary flex-1" disabled={voidQuote.isPending} onClick={handleVoid}>
                      Anular cotización
                    </button>
                    <button type="button" className="btn-primary flex-1" disabled={!openShift || convertQuote.isPending} onClick={handleConvert}>
                      {convertQuote.isPending ? "Convirtiendo..." : "Convertir en venta"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface DraftLine {
  productId: string;
  quantity: string;
}

function CreateQuoteModal({ onClose }: { onClose: () => void }) {
  const { data: branches } = useBranches(true);
  const { data: productsResponse } = useProducts({ pageSize: 200, isActive: true });
  const products = productsResponse?.data ?? [];
  const createQuote = useCreateQuote();

  const [branchId, setBranchId] = useState("");
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerLabel, setCustomerLabel] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([{ productId: "", quantity: "1" }]);
  const customerInputRef = useRef<HTMLInputElement>(null);

  const { data: customerMatches } = useStoreCustomers(customerSearch.length >= 2 ? customerSearch : undefined);

  useEffect(() => {
    if (!branchId && branches && branches.length > 0) {
      setBranchId(branches.find((b) => b.isMain)?.id ?? branches[0].id);
    }
  }, [branches, branchId]);

  const addLine = () => setLines((prev) => [...prev, { productId: "", quantity: "1" }]);
  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index));
  const updateLine = (index: number, patch: Partial<DraftLine>) => setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const isValid = branchId && lines.every((l) => l.productId && Number(l.quantity) > 0);

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error("Elige sucursal y completa cada línea con producto y cantidad.");
      return;
    }
    try {
      await createQuote.mutateAsync({
        branchId,
        storeCustomerId: customerId ?? undefined,
        customerName: customerId ? undefined : customerName || undefined,
        customerPhone: customerId ? undefined : customerPhone || undefined,
        validUntil: validUntil || undefined,
        notes: notes || undefined,
        lines: lines.map((l) => ({ productId: l.productId, quantity: l.quantity })),
      });
      toast.success("Cotización creada.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Nueva cotización</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Sucursal</label>
              <select className="input-field mt-1" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">Seleccione...</option>
                {branches?.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Vigente hasta (opcional)</label>
              <input className="input-field mt-1" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Cliente (opcional)</label>
            {customerId ? (
              <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
                <span className="truncate text-gray-900">{customerLabel}</span>
                <button type="button" className="shrink-0 text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => { setCustomerId(null); setCustomerLabel(""); }}>
                  Cambiar
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <input
                    ref={customerInputRef}
                    className="input-field"
                    placeholder="Buscar cliente registrado por nombre o cédula..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                  />
                  {customerSearch.length >= 2 && (customerMatches?.length ?? 0) > 0 && (
                    <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                      {customerMatches!.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                          onClick={() => {
                            setCustomerId(c.id);
                            setCustomerLabel(`${c.name}${c.documentId ? ` (${c.documentId})` : ""}`);
                            setCustomerSearch("");
                          }}
                        >
                          {c.name} {c.documentId ? `(${c.documentId})` : ""}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400">O, si no tiene cuenta registrada, deja un nombre/teléfono de referencia:</p>
                <div className="grid grid-cols-2 gap-2">
                  <input className="input-field" placeholder="Nombre" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                  <input className="input-field" placeholder="Teléfono" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">Productos</p>
              <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={addLine}>
                + Agregar línea
              </button>
            </div>
            <div className="space-y-2">
              {lines.map((line, index) => (
                <div key={index} className="grid grid-cols-12 items-center gap-2">
                  <select className="input-field col-span-8" value={line.productId} onChange={(e) => updateLine(index, { productId: e.target.value })}>
                    <option value="">Producto...</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.sku} — {p.name} ({currency.format(Number(p.salePrice))})
                      </option>
                    ))}
                  </select>
                  <input
                    className="input-field col-span-3"
                    type="number"
                    placeholder="Cantidad"
                    value={line.quantity}
                    onChange={(e) => updateLine(index, { quantity: e.target.value })}
                  />
                  <button type="button" className="col-span-1 text-gray-400 hover:text-red-600" onClick={() => removeLine(index)} aria-label="Quitar línea">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Notas (opcional)</label>
            <textarea className="input-field mt-1" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={!isValid || createQuote.isPending} onClick={handleSubmit}>
            {createQuote.isPending ? "Guardando..." : "Crear cotización"}
          </button>
        </div>
      </div>
    </div>
  );
}
